import { useState, useCallback, useRef } from "react";
import {
  agentsGetSpecificAgentResponse,
  agentsGetWayakResponse,
  threadsCreateThread,
  type PersistentThread,
  type PersistentMessage,
} from "../sdk";

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface UseDashboardAgentProps {
  dashboardId: string;
  dashboardContext: string;
  orgId: string;
  userId: string; // Required for creating threads
  agentId?: string; // Optional specific agent, otherwise uses default Wayak agent
}

interface UseDashboardAgentReturn {
  messages: Message[];
  sendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  threadId: string | null;
  clearMessages: () => void;
}

export function useDashboardAgent({
  dashboardId,
  dashboardContext,
  orgId,
  userId,
  agentId
}: UseDashboardAgentProps): UseDashboardAgentReturn {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "placeholder",
      role: "assistant",
      content: "I'm ready to help you explore this dashboard. I have context about all the views and data sources. What would you like to know?",
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const isInitializingRef = useRef(false);

  // Initialize thread and send context (only called on first message)
  const initializeThread = async (): Promise<string> => {
    if (isInitializingRef.current) {
      throw new Error("Thread initialization already in progress");
    }

    isInitializingRef.current = true;

    try {
      // Create a new thread for this dashboard session
      const response = await threadsCreateThread({
        body: {
          user_id: userId,
          title: `Dashboard ${dashboardId} Chat`,
          description: "Agent chat for dashboard analysis",
          model: "claude-3-5-sonnet-20241022", // Default model
          org_id: orgId
        }
      });

      const thread = response.data;
      if (!thread || !thread.id) {
        throw new Error("Thread created but no ID returned");
      }

      setThreadId(thread.id);

      // Send initial context message to thread
      await sendInitialContext(thread.id);

      return thread.id;
    } catch (err: any) {
      console.error("Failed to initialize agent thread:", err);
      throw new Error("Failed to initialize chat");
    } finally {
      isInitializingRef.current = false;
    }
  };

  // Send initial context to agent
  const sendInitialContext = async (tid: string) => {
    try {
      // Send context as a system message
      const contextMessage = {
        content: dashboardContext,
        role: "system"
      };

      if (agentId) {
        await agentsGetSpecificAgentResponse({
          path: { agentId },
          body: {
            messages: [contextMessage],
            threadId: tid,
            orgId,
            saveMessages: true
          }
        });
      } else {
        await agentsGetWayakResponse({
          body: {
            messages: [contextMessage],
            threadId: tid,
            orgId,
            saveMessages: true
          }
        });
      }
    } catch (err) {
      console.error("Failed to send context:", err);
      // Don't fail initialization if context send fails
    }
  };

  // Send a message
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Remove placeholder message if it exists
    setMessages(prev => prev.filter(m => m.id !== "placeholder"));

    // Add user message immediately
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Initialize thread if this is the first message
      let currentThreadId = threadId;
      if (!currentThreadId) {
        currentThreadId = await initializeThread();
      }

      // Send message to agent
      const message = {
        content,
        role: "user"
      };

      let apiResponse;
      if (agentId) {
        apiResponse = await agentsGetSpecificAgentResponse({
          path: { agentId },
          body: {
            messages: [message],
            threadId: currentThreadId,
            orgId,
            saveMessages: true
          }
        });
      } else {
        apiResponse = await agentsGetWayakResponse({
          body: {
            messages: [message],
            threadId: currentThreadId,
            orgId,
            saveMessages: true
          }
        });
      }

      const response = apiResponse.data;
      if (!response) {
        throw new Error("No response data");
      }

      // Extract content from response
      let responseContent = "I apologize, but I couldn't generate a response.";

      if (response.content) {
        if (typeof response.content === "string") {
          responseContent = response.content;
        } else if (typeof response.content === "object") {
          // Handle structured content
          responseContent = JSON.stringify(response.content, null, 2);
        }
      }

      // Add assistant response
      const assistantMessage: Message = {
        id: response.id || `assistant-${Date.now()}`,
        role: "assistant",
        content: responseContent,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error("Failed to send message:", err);
      setError(err?.message || "Failed to send message");

      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "I apologize, but I encountered an error processing your message. Please try again.",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [threadId, orgId, agentId, dashboardId, dashboardContext, userId]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([{
      id: "placeholder",
      role: "assistant",
      content: "I'm ready to help you explore this dashboard. I have context about all the views and data sources. What would you like to know?",
      timestamp: new Date()
    }]);
    setError(null);
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    threadId,
    clearMessages
  };
}
